"use client";

import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Sparkles, Zap, CheckCircle, Check, Crown, Clock, XCircle, RefreshCw } from "lucide-react";
import { getCheckoutUrlAction, cancelSubscriptionAction, resumeSubscriptionAction } from '@/lib/payments/actions';
import { STRIPE_PRICE_IDS } from '@/lib/payments/config';
import { useI18n } from '@/lib/i18n';
import { useUsage } from '@/contexts/usage-context';
import { useUser } from '@/lib/context/user-context';

// Billing Cycle Switcher Component with ref-based animation
function BillingCycleSwitcher({
  value,
  onChange,
  monthlyLabel,
  annuallyLabel,
}: {
  value: 'monthly' | 'annually';
  onChange: (value: 'monthly' | 'annually') => void;
  monthlyLabel: string;
  annuallyLabel: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const monthlyRef = useRef<HTMLButtonElement>(null);
  const annuallyRef = useRef<HTMLButtonElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const updateIndicator = () => {
      const container = containerRef.current;
      const activeButton = value === 'monthly' ? monthlyRef.current : annuallyRef.current;
      
      if (container && activeButton) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        setIndicatorStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
        });
      }
    };

    updateIndicator();
    // Update on resize
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [value]);

  return (
    <div className="flex items-center justify-center">
      <div 
        ref={containerRef}
        className="relative inline-flex items-center p-0.5 bg-muted/50 rounded-full border border-border/50"
      >
        {/* Animated indicator */}
        <motion.div
          className="absolute top-0 bottom-0 bg-background rounded-full shadow-sm"
          initial={false}
          animate={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35,
          }}
        />
        <button
          ref={monthlyRef}
          type="button"
          onClick={() => onChange('monthly')}
          className={`relative z-10 px-3 sm:px-4 py-0.5 text-xs sm:text-sm font-medium rounded-full transition-colors duration-200 ${
            value === 'monthly'
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {monthlyLabel}
        </button>
        <button
          ref={annuallyRef}
          type="button"
          onClick={() => onChange('annually')}
          className={`relative z-10 px-3 sm:px-4 py-0.5 text-xs sm:text-sm font-medium rounded-full transition-colors duration-200 flex items-center gap-1 sm:gap-1.5 ${
            value === 'annually'
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {annuallyLabel}
          <motion.span 
            className="text-[9px] sm:text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-1 sm:px-1.5 py-0.5 rounded-full"
            animate={{
              scale: value === 'annually' ? [1, 1.1, 1] : 1,
            }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
          >
            -40%
          </motion.span>
        </button>
      </div>
    </div>
  );
}

interface UpgradePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  errorType?: 'limit_exceeded' | 'authentication' | 'general';
  remainingCount?: number;
  limitCount?: number;
  errorMessage?: string;
  onUpgrade?: () => void;
}

export function UpgradePromptDialog({
  isOpen,
  onClose,
  errorType = 'limit_exceeded',
  remainingCount = 0,
  limitCount = 3,
  errorMessage,
  onUpgrade
}: UpgradePromptDialogProps) {
  const { t } = useI18n();
  const { usageInfo } = useUsage();
  const { user } = useUser();
  const isLimitExceeded = errorType === 'limit_exceeded';
  
  // Get current user plan info
  const currentPlanType = usageInfo?.planType;
  const currentPlanName = usageInfo?.planName;
  
  // Get billing cycle from user_metadata, or infer from planName as fallback
  const currentBillingCycle = (() => {
    // First try user_metadata
    const metadataCycle = user?.user_metadata?.billingCycle as 'monthly' | 'yearly' | undefined;
    if (metadataCycle) return metadataCycle;
    
    // Fallback: infer from planName (e.g., "Basic Plan Annually" -> "yearly")
    if (currentPlanName) {
      const lowerName = currentPlanName.toLowerCase();
      if (lowerName.includes('annually') || lowerName.includes('yearly') || lowerName.includes('year')) {
        return 'yearly' as const;
      }
      if (lowerName.includes('monthly') || lowerName.includes('month')) {
        return 'monthly' as const;
      }
    }
    
    return undefined;
  })();
  
  // Determine current plan (basic or premium)
  const isCurrentPlanBasic = currentPlanType === 'basic' || currentPlanType === 'basic_lite';
  const isCurrentPlanPremium = currentPlanType === 'premium';
  
  // Check if subscription is set to cancel at period end
  const isCancelledAtPeriodEnd = user?.user_metadata?.subscriptionCancelAtPeriodEnd === true;
  const subscriptionEndDate = user?.user_metadata?.subscriptionEndDate;
  
  // Initialize selected plan - default to premium, but if user is basic, default to premium (upgrade)
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'premium'>(() => {
    if (isCurrentPlanBasic) return 'premium'; // Basic users should see premium as default
    return 'premium'; // Default to premium
  });
  
  // Initialize billing cycle - use current if available, otherwise default to annually
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annually'>('annually');
  
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  
  // Sync billing cycle when user data loads
  useEffect(() => {
    if (currentBillingCycle === 'yearly') {
      setBillingCycle('annually');
    } else if (currentBillingCycle === 'monthly') {
      setBillingCycle('monthly');
    }
  }, [currentBillingCycle]);
  
  // Check if user is on a paid plan (limitCount === -1 indicates unlimited/premium)
  const isFreePlan = limitCount > 0; // Free plan has a positive limit, premium has -1
  
  // Check if current billing cycle matches the selected billing cycle
  const currentBillingCycleMatches = 
    (currentBillingCycle === 'yearly' && billingCycle === 'annually') ||
    (currentBillingCycle === 'monthly' && billingCycle === 'monthly');
  
  // Check if plan is the user's current plan (only when billing cycle also matches)
  const isBasicCurrent = isCurrentPlanBasic && currentBillingCycleMatches;
  const isPremiumCurrent = isCurrentPlanPremium && currentBillingCycleMatches;
  
  // Check if user selected their current plan (planType AND billingCycle match)
  const isSelectedPlanCurrent = 
    (selectedPlan === 'basic' && isBasicCurrent) || 
    (selectedPlan === 'premium' && isPremiumCurrent);
  
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  
  const usedCount = Math.max(0, limitCount - remainingCount);
  const usageSummary = t('upgradePromptDialog.usage.summary', '{used}/{limit} transcripts used this month')
    .replace('{used}', String(usedCount))
    .replace('{limit}', String(limitCount));
  const usageRemaining = t('upgradePromptDialog.usage.remaining', '{remaining}/{limit} remaining')
    .replace('{remaining}', String(Math.max(0, remainingCount)))
    .replace('{limit}', String(limitCount));

  const planCopy = {
    basic: {
      name: t('upgradePromptDialog.plans.basic.name', 'Basic Plan'),
      short: t('upgradePromptDialog.plans.basic.short', 'Basic'),
      badge: t('upgradePromptDialog.plans.basic.badge', 'Popular'),
      monthly: {
        price: '$16.99',
        period: '/month',
      },
      annually: {
        price: '$9.99',
        originalPrice: '$16.99',
        period: '/month',
        billedTotal: '$119.88',
      },
      description: t('upgradePromptDialog.plans.basic.description', 'Perfect for individual learners'),
    },
    premium: {
      name: t('upgradePromptDialog.plans.premium.name', 'Premium Plan'),
      short: t('upgradePromptDialog.plans.premium.short', 'Premium'),
      badge: t('upgradePromptDialog.plans.premium.badge', 'Best Value'),
      monthly: {
        price: '$28.99',
        period: '/month',
      },
      annually: {
        price: '$18.99',
        originalPrice: '$28.99',
        period: '/month',
        billedTotal: '$227.88',
      },
      description: t('upgradePromptDialog.plans.premium.description', 'Advanced features & priority support'),
    },
  } as const;

  const upgradeBenefitTitle = selectedPlan === 'basic'
    ? t('upgradePromptDialog.benefits.titleBasic', `Upgrade to ${planCopy.basic.short}`)
    : t('upgradePromptDialog.benefits.titlePremium', `Upgrade to ${planCopy.premium.short}`);
  
  // Different transcript limits based on plan
  const transcriptLimitText = selectedPlan === 'basic' 
    ? t('upgradePromptDialog.benefits.items.basicTranscripts', '1000 minutes transcription per month')
    : t('upgradePromptDialog.benefits.items.premiumTranscripts', '2000 minutes transcription per month');
  
  const benefitsFallbacks: Record<string, string> = {
    aiChat: '30 AI Chat with episodes per day',
    aiSummaries: 'Watch all Ready transcribed videos',
    vocabulary: 'Full vocabulary analysis',
    priority: 'Priority processing',
    export: 'Export to all formats',
  };
  const benefitKeys = Object.keys(benefitsFallbacks);
  const benefits = [
    transcriptLimitText,
    ...benefitKeys.map((key) =>
      t(`upgradePromptDialog.benefits.items.${key}`, benefitsFallbacks[key])
    )
  ];

  const upgradeCta = t('upgradePromptDialog.actions.upgradeToPlan', 'Upgrade to {plan}')
    .replace('{plan}', selectedPlan === 'basic' ? planCopy.basic.short : planCopy.premium.short);
  const continuePremiumCta = t('upgradePromptDialog.actions.continueWithPlan', 'Continue with {plan}')
    .replace('{plan}', planCopy.premium.short);

  const handleUpgrade = async () => {
    // If selecting current plan, don't proceed with upgrade
    if (isSelectedPlanCurrent) {
      return;
    }
    
    setIsCheckingOut(true);
    try {
      const priceId = billingCycle === 'annually' 
        ? STRIPE_PRICE_IDS[selectedPlan].yearly 
        : STRIPE_PRICE_IDS[selectedPlan].monthly;
      const checkoutUrl = await getCheckoutUrlAction(priceId);
      
      // Open Stripe checkout in a new tab
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      onUpgrade?.();
    } catch (error) {
      console.error('Checkout error:', error);
      alert(t('upgradePromptDialog.alerts.checkoutFailed', 'Failed to start checkout. Please try again.'));
    } finally {
      setIsCheckingOut(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    if (!user?.id) {
      alert(t('upgradePromptDialog.alerts.notAuthenticated', 'Please log in to manage your subscription.'));
      return;
    }
    
    if (!confirm(t('upgradePromptDialog.alerts.confirmCancel', 'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.'))) {
      return;
    }
    
    setIsCancelling(true);
    try {
      const result = await cancelSubscriptionAction(user.id);
      if (result.success) {
        alert(t('upgradePromptDialog.alerts.cancelSuccess', 'Your subscription has been cancelled. You will retain access until the end of your billing period.'));
        onClose();
        // Refresh the page to update UI
        window.location.reload();
      }
    } catch (error) {
      console.error('Cancel subscription error:', error);
      alert(t('upgradePromptDialog.alerts.cancelFailed', 'Failed to cancel subscription. Please try again or contact support.'));
    } finally {
      setIsCancelling(false);
    }
  };
  
  const handleResumeSubscription = async () => {
    if (!user?.id) {
      alert(t('upgradePromptDialog.alerts.notAuthenticated', 'Please log in to manage your subscription.'));
      return;
    }
    
    setIsResuming(true);
    try {
      const result = await resumeSubscriptionAction(user.id);
      if (result.success) {
        alert(t('upgradePromptDialog.alerts.resumeSuccess', 'Your subscription has been resumed successfully!'));
        onClose();
        window.location.reload();
      }
    } catch (error) {
      console.error('Resume subscription error:', error);
      alert(t('upgradePromptDialog.alerts.resumeFailed', 'Failed to resume subscription. Please try again or contact support.'));
    } finally {
      setIsResuming(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
        {/* Header with animated gradient background */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 via-primary/15 to-amber-500/20 dark:from-violet-600/30 dark:via-primary/20 dark:to-amber-600/30" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-amber-400/20 to-transparent rounded-full blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-violet-400/20 to-transparent rounded-full blur-2xl" />
          
          <DialogHeader className="relative p-5 pb-4">
            <div className="flex items-center gap-4">
              {isLimitExceeded ? (
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-amber-500/40 rounded-xl blur-md" />
                  <div className="relative p-1 bg-background/90 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg">
                    <Image 
                      src="/logo1.png" 
                      alt="Logo" 
                      width={44} 
                      height={44}
                      className="w-10 h-10"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-red-100/90 dark:bg-red-900/40 backdrop-blur-sm rounded-xl border border-red-200/50 dark:border-red-800/50 shadow-lg">
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              )}
              <div className="flex-1">
                <DialogTitle className="text-xl font-bold text-foreground pr-8 tracking-tight">
                  {isLimitExceeded 
                    ? (remainingCount === 0 
                        ? t('upgradePromptDialog.titles.limitReached', 'Monthly Limit Reached')
                        : t('upgradePromptDialog.titles.upgrade', 'Unlock More Power'))
                    : t('upgradePromptDialog.titles.error', 'Generation Failed')}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  {isLimitExceeded 
                    ? t('upgradePromptDialog.subtitles.choosePlan', 'Choose a plan that fits your needs')
                    : errorMessage || t('upgradePromptDialog.error.generic', 'Unable to generate transcript')}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>
        
        {/* Main content */}
        <div className="px-3 sm:px-5 pb-2">
          {isLimitExceeded && (
            <>
              {isFreePlan ? (
                <div className="space-y-4">
                  {/* Billing Cycle Switcher */}
                  <BillingCycleSwitcher
                    value={billingCycle}
                    onChange={setBillingCycle}
                    monthlyLabel={t('upgradePromptDialog.billing.monthly', 'Monthly')}
                    annuallyLabel={t('upgradePromptDialog.billing.annually', 'Annually')}
                  />

                  {/* Current Plan Indicator */}
                  {(isCurrentPlanBasic || isCurrentPlanPremium) && (
                    <div className="mb-2 px-3 py-2.5 rounded-xl bg-muted/50 border border-border/50">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs">
                          <div className={`w-2 h-2 rounded-full ${isCancelledAtPeriodEnd ? 'bg-orange-400 animate-pulse' : 'bg-primary'}`} />
                          <span className="text-muted-foreground">
                            {currentPlanName || (isCurrentPlanPremium ? planCopy.premium.name : planCopy.basic.name)}
                            {currentBillingCycle && (
                              <span className="text-muted-foreground/70">
                                {' · '}{currentBillingCycle === 'yearly' ? t('upgradePromptDialog.billing.annually', 'Annually') : t('upgradePromptDialog.billing.monthly', 'Monthly')}
                              </span>
                            )}
                            {isCancelledAtPeriodEnd && subscriptionEndDate && (
                              <span className="text-muted-foreground/70">
                                {' · '}{t('upgradePromptDialog.expiresOn', 'Expires {date}').replace('{date}', new Date(subscriptionEndDate).toLocaleDateString())}
                              </span>
                            )}
                          </span>
                        </div>
                        {isCancelledAtPeriodEnd && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleResumeSubscription}
                            disabled={isResuming}
                            className="h-6 px-2.5 text-[10px] text-primary hover:bg-primary/10 hover:text-primary"
                          >
                            <RefreshCw className={`h-3 w-3 mr-1 ${isResuming ? 'animate-spin' : ''}`} />
                            {isResuming ? t('upgradePromptDialog.status.resuming', 'Resuming...') : t('upgradePromptDialog.actions.resume', 'Resume')}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Plan Cards - Horizontal Layout */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    {/* Basic Plan Card */}
                    <div 
                      className="relative group cursor-pointer"
                      onClick={() => setSelectedPlan('basic')}
                    >
                      <div className={`relative h-full rounded-xl p-3 sm:p-4 border-2 transition-all duration-300 ${
                        isBasicCurrent 
                          ? selectedPlan === 'basic'
                            ? 'border-[#C5A572] bg-[#C5A572]/10 shadow-lg shadow-[#C5A572]/10' 
                            : 'border-[#C5A572]/40 bg-[#C5A572]/5 hover:border-[#C5A572]/60'
                          : selectedPlan === 'basic' 
                            ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10' 
                            : 'border-border/60 hover:border-primary/40 hover:bg-muted/30'
                      }`}>
                        {/* Current indicator - always visible for current plan */}
                        {isBasicCurrent && (
                          <div className="absolute -top-2.5 -right-2.5 z-20">
                            <div className="bg-[#C5A572] text-white px-2.5 py-1 rounded-tr-xl rounded-bl-xl shadow-md border border-white/20 flex items-center justify-center">
                              <span className="text-[10px] sm:text-xs font-bold tracking-wide leading-none">
                                {t('upgradePromptDialog.current', 'Current')}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Selection indicator - only for non-current plans */}
                        {!isBasicCurrent && (
                          <div className={`absolute -top-2.5 -right-2.5 z-20 transition-all duration-300 ${
                            selectedPlan === 'basic' ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                          }`}>
                            <div className="bg-primary rounded-full p-1.5 sm:p-2 shadow-lg">
                              <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-foreground" strokeWidth={3} />
                            </div>
                          </div>
                        )}
                        
                        {/* Badge */}
                        <div className="flex items-center gap-2 mb-2 sm:mb-3">
                          <span className={`text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 rounded-full ${
                            isBasicCurrent 
                              ? 'bg-[#C5A572]/15 text-[#C5A572]' 
                              : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                          }`}>
                            {isBasicCurrent ? t('upgradePromptDialog.yourPlan', 'Your Plan') : planCopy.basic.badge}
                          </span>
                        </div>
                        
                        {/* Plan name */}
                        <h3 className="font-semibold text-foreground text-sm sm:text-base mb-1">{planCopy.basic.name}</h3>
                        
                        {/* Price */}
                        <div className="mb-1">
                          <div className="flex items-baseline flex-wrap gap-x-1">
                            <span className="text-xl sm:text-2xl font-bold text-foreground">
                              {billingCycle === 'annually' ? planCopy.basic.annually.price : planCopy.basic.monthly.price}
                            </span>
                            {billingCycle === 'annually' && (
                              <span className="text-xs sm:text-sm text-muted-foreground line-through">
                                {planCopy.basic.annually.originalPrice}
                              </span>
                            )}
                            <span className="text-[10px] sm:text-xs text-muted-foreground">/mo</span>
                          </div>
                          {billingCycle === 'annually' && (
                            <div className="mt-1.5">
                              <span className="text-[10px] sm:text-xs font-medium text-primary bg-primary/10 px-1.5 sm:px-2 py-0.5 rounded-full inline-block">
                                {planCopy.basic.annually.billedTotal}
                              </span>
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                {t('upgradePromptDialog.billing.billedAnnually', 'Billed Annually')}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Transcript limit highlight */}
                        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border/50">
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary shrink-0" />
                            <div className="flex items-baseline gap-1">
                              <span className="text-base sm:text-lg font-bold text-primary">1000</span>
                              <span className="text-[10px] sm:text-xs text-muted-foreground">min/mo</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Premium Plan Card */}
                    <div 
                      className="relative group cursor-pointer"
                      onClick={() => setSelectedPlan('premium')}
                    >
                      {/* Glow effect for premium */}
                      <div className={`absolute -inset-0.5 bg-gradient-to-br from-amber-400 via-primary to-violet-500 rounded-xl blur opacity-0 transition-opacity duration-300 ${
                        selectedPlan === 'premium' ? 'opacity-30' : 'group-hover:opacity-20'
                      }`} />
                      
                      <div className={`relative h-full rounded-xl p-3 sm:p-4 border-2 transition-all duration-300 bg-background ${
                        isPremiumCurrent 
                          ? selectedPlan === 'premium'
                            ? 'border-[#C5A572] bg-[#C5A572]/10 shadow-lg shadow-[#C5A572]/10' 
                            : 'border-[#C5A572]/40 bg-[#C5A572]/5 hover:border-[#C5A572]/60'
                          : selectedPlan === 'premium' 
                            ? 'border-primary/50 bg-gradient-to-br from-primary/10 via-background to-amber-500/10 shadow-lg' 
                            : 'border-border/60 hover:border-primary/40'
                      }`}>
                        {/* Current indicator - always visible for current plan */}
                        {isPremiumCurrent && (
                          <div className="absolute -top-2.5 -right-2.5 z-20">
                            <div className="bg-gradient-to-r from-[#C5A572] to-[#B89660] text-white px-2.5 py-1 rounded-tr-xl rounded-bl-xl shadow-md border border-white/20 flex items-center justify-center">
                              <span className="text-[10px] sm:text-xs font-bold tracking-wide leading-none">
                                {t('upgradePromptDialog.current', 'Current')}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Selection indicator - only for non-current plans */}
                        {!isPremiumCurrent && (
                          <div className={`absolute -top-2.5 -right-2.5 z-20 transition-all duration-300 ${
                            selectedPlan === 'premium' ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                          }`}>
                            <div className="bg-gradient-to-br from-amber-400 to-primary rounded-full p-1.5 sm:p-2 shadow-lg">
                              <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" strokeWidth={3} />
                            </div>
                          </div>
                        )}
                        
                        {/* Badge */}
                        <div className="flex items-center gap-2 mb-2 sm:mb-3">
                          <span className={`text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 rounded-full flex items-center gap-1 ${
                            isPremiumCurrent 
                              ? 'bg-[#C5A572]/15 text-[#C5A572]' 
                              : 'bg-gradient-to-r from-amber-500/20 to-primary/20 text-amber-600 dark:text-amber-400'
                          }`}>
                            <Crown className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                            {isPremiumCurrent ? t('upgradePromptDialog.yourPlan', 'Your Plan') : planCopy.premium.badge}
                          </span>
                        </div>
                        
                        {/* Plan name */}
                        <h3 className="font-semibold text-foreground text-sm sm:text-base mb-1">{planCopy.premium.name}</h3>
                        
                        {/* Price */}
                        <div className="mb-1">
                          <div className="flex items-baseline flex-wrap gap-x-1">
                            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
                              {billingCycle === 'annually' ? planCopy.premium.annually.price : planCopy.premium.monthly.price}
                            </span>
                            {billingCycle === 'annually' && (
                              <span className="text-xs sm:text-sm text-muted-foreground line-through">
                                {planCopy.premium.annually.originalPrice}
                              </span>
                            )}
                            <span className="text-[10px] sm:text-xs text-muted-foreground">/mo</span>
                          </div>
                          {billingCycle === 'annually' && (
                            <div className="mt-1.5">
                              <span className="text-[10px] sm:text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1.5 sm:px-2 py-0.5 rounded-full inline-block">
                                {planCopy.premium.annually.billedTotal}
                              </span>
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                {t('upgradePromptDialog.billing.billedAnnually', 'Billed Annually')}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Transcript limit highlight */}
                        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-amber-500/20">
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-500 shrink-0" />
                            <div className="flex items-baseline gap-1">
                              <span className="text-base sm:text-lg font-bold bg-gradient-to-r from-amber-500 to-primary bg-clip-text text-transparent">2000</span>
                              <span className="text-[10px] sm:text-xs text-muted-foreground">min/mo</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Benefits Section */}
                  <div className="relative rounded-xl p-4 bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-1.5 bg-primary/15 rounded-lg">
                        <Sparkles className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-sm text-foreground">
                        {upgradeBenefitTitle}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {benefits.map((benefit, index) => (
                        <div 
                          key={benefit}
                          className="flex items-center gap-2 text-sm"
                        >
                          <CheckCircle className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                          <span className="text-muted-foreground text-xs">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* For premium users, show a success message instead */
                <div className="relative rounded-xl p-4 bg-gradient-to-br from-emerald-500/10 to-primary/10 border border-emerald-500/20">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{t('upgradePromptDialog.premiumStatus.title', 'You have a Premium Plan')}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {t('upgradePromptDialog.premiumStatus.description', 'Enjoy unlimited transcript generation and all premium features.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {errorType === 'authentication' && (
          <div className="relative rounded-xl p-4 bg-amber-500/10 border border-amber-500/20 mx-5">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                {t('upgradePromptDialog.error.loginRequired', 'Please log in to generate transcripts. Your progress will be saved.')}
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="p-5 pt-3 gap-3 flex-col sm:flex-row bg-muted/20">
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="order-2 sm:order-1 text-muted-foreground hover:text-foreground hover:bg-muted/50 h-10 text-sm font-medium"
          >
            {isLimitExceeded 
              ? t('upgradePromptDialog.actions.later', 'Maybe Later')
              : t('upgradePromptDialog.actions.close', 'Close')}
          </Button>
          
          {isLimitExceeded ? (
            isFreePlan ? (
              isSelectedPlanCurrent ? (
                isCancelledAtPeriodEnd ? (
                  <Button 
                    onClick={handleResumeSubscription}
                    disabled={isResuming}
                    className="order-1 sm:order-2 flex-1 h-11 text-sm font-semibold transition-all duration-300 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isResuming ? 'animate-spin' : ''}`} />
                    {isResuming 
                      ? t('upgradePromptDialog.status.resuming', 'Resuming...')
                      : t('upgradePromptDialog.actions.resumeSubscription', 'Resume Subscription')}
                  </Button>
                ) : (
                  <Button 
                    onClick={handleCancelSubscription}
                    disabled={isCancelling}
                    variant="outline"
                    className="order-1 sm:order-2 flex-1 h-11 text-sm font-semibold transition-all duration-300 border-destructive/30 text-destructive hover:bg-destructive/10 hover:border-destructive/50"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {isCancelling 
                      ? t('upgradePromptDialog.status.cancelling', 'Cancelling...')
                      : t('upgradePromptDialog.actions.cancelSubscription', 'Cancel Subscription')}
                  </Button>
                )
              ) : (
                <Button 
                  onClick={handleUpgrade}
                  disabled={isCheckingOut}
                  className={`order-1 sm:order-2 flex-1 h-11 text-sm font-semibold transition-all duration-300 ${
                    selectedPlan === 'premium'
                    ? 'bg-gradient-to-r from-amber-500 via-primary to-violet-500 hover:from-amber-600 hover:via-primary/90 hover:to-violet-600 text-white shadow-lg hover:shadow-xl hover:shadow-primary/20'
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg'
                  }`}
                >
                  <Zap className="h-4 w-4 mr-2" />
                  {isCheckingOut 
                    ? t('upgradePromptDialog.status.processing', 'Processing...')
                    : upgradeCta}
                </Button>
              )
            ) : (
              <Button 
                onClick={onClose}
                className="order-1 sm:order-2 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11 text-sm font-semibold shadow-md"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {continuePremiumCta}
              </Button>
            )
          ) : errorType === 'authentication' ? (
            <Button 
              onClick={() => window.location.href = '/auth'}
              className="order-1 sm:order-2 flex-1 bg-primary hover:bg-primary/90 text-primary-foreground h-11 text-sm font-semibold"
            >
              {t('common.signIn')}
            </Button>
          ) : (
            <Button 
              onClick={onClose}
              className="order-1 sm:order-2 w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 text-sm font-semibold"
            >
              {t('upgradePromptDialog.actions.tryAgain', 'Try Again')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
